import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { WarningCircle, X, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ClientReminderModal from "@/components/ClientReminderModal";
import { money } from "@/lib/money";


export const BUCKETS = [
  { key: "under_30", label: "< 30 days", tone: "emerald" },
  { key: "30_60", label: "30 – 60", tone: "yellow" },
  { key: "60_90", label: "60 – 90", tone: "orange" },
  { key: "over_90", label: "90+", tone: "rose" },
];

const bucketBg = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  yellow: "bg-amber-50 border-amber-200 text-amber-800",
  orange: "bg-orange-50 border-orange-200 text-orange-800",
  rose: "bg-rose-50 border-rose-200 text-rose-800",
};

const toneText = { emerald: "text-emerald-700", yellow: "text-amber-700", orange: "text-orange-700", rose: "text-rose-700" };
const REMIND_BUCKETS = new Set(["60_90", "over_90"]);
const shortDT = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

// Pop-up listing every invoice (client + balance) that makes up one aging bucket.
// `clientName` narrows it to one client (from the by-client table). In the 60–90 / 90+
// ranges admins can chase late invoices right here (per row or all at once).
function AgingBucketPopup({ bucket, invoices, total, clientName, client, onClose, onRefresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [consolidated, setConsolidated] = useState(false);
  const minAge = bucket.key === "over_90" ? 90 : 60;
  const [busyId, setBusyId] = useState("");
  const [bulk, setBulk] = useState(null); // {done, total}
  const [sentNow, setSentNow] = useState({}); // id -> iso
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busyId && !bulk && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busyId, bulk]);
  const clients = new Set(invoices.map((i) => i.client_id)).size;
  const canChase = isAdmin && REMIND_BUCKETS.has(bucket.key);
  const remindable = invoices.filter((i) => i.can_remind);
  const lastReminded = (i) => sentNow[i.id] || i.last_reminded_at;

  const remindOne = async (i, silent = false) => {
    if (!silent) setBusyId(i.id);
    try {
      const { data } = await api.post(`/invoices/${i.id}/remind`);
      setSentNow((s) => ({ ...s, [i.id]: new Date().toISOString() }));
      if (!silent) toast.success(data.email_sent ? `Reminder for ${i.number} sent to ${data.to}.` : `Reminder for ${i.number} recorded, but the email failed — check Sent Emails.`);
      return data.email_sent !== false;
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail?.toString() || e.message);
      return false;
    } finally { if (!silent) setBusyId(""); }
  };
  const remindAll = async () => {
    const already = remindable.filter((i) => lastReminded(i) && (Date.now() - new Date(lastReminded(i)).getTime()) < 86400000).length;
    if (!window.confirm(`Send a payment reminder email for all ${remindable.length} invoice${remindable.length === 1 ? "" : "s"} in the ${bucket.label} range${clientName ? ` for ${clientName}` : ""}?${already ? `\n\n${already} of them already got a reminder in the last 24 hours — they will be reminded again.` : ""}\n\nEach client gets one email per invoice.`)) return;
    setBulk({ done: 0, total: remindable.length });
    let ok = 0; const failed = [];
    for (const i of remindable) {
      (await remindOne(i, true)) ? ok++ : failed.push(i.number);
      setBulk((b) => ({ ...b, done: b.done + 1 }));
    }
    setBulk(null);
    if (ok) toast.success(`${ok} reminder${ok === 1 ? "" : "s"} sent.`);
    if (failed.length) toast.error(`Failed for ${failed.join(", ")} — check Sent Emails.`, { duration: 10000 });
    onRefresh?.();
  };
  const close = () => { if (Object.keys(sentNow).length) onRefresh?.(); onClose(); };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" onClick={() => !busyId && !bulk && close()} data-testid="aging-popup">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-3xl border border-slate-200 shadow-xl max-h-[85vh] flex flex-col">
        <div className={`px-5 py-3 border-b flex items-center justify-between gap-3 ${bucketBg[bucket.tone]}`}>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">Receivables aging · {bucket.label}{bucket.key === "undated" ? "" : " days"}{clientName ? ` · ${clientName}` : ""}</div>
            <div className="text-lg font-black font-mono" data-testid="aging-popup-total">{money(total)} <span className="text-xs font-sans font-semibold opacity-80">· {invoices.length} invoice{invoices.length === 1 ? "" : "s"}{clientName ? "" : ` · ${clients} client${clients === 1 ? "" : "s"}`}</span></div>
          </div>
          <div className="flex items-center gap-2">
            {canChase && remindable.length > 0 && client && (
              <button type="button" onClick={() => setConsolidated(true)} disabled={!!bulk || !!busyId} data-testid="aging-remind-client-one"
                title="One email to this client listing all of these invoices"
                className="h-9 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60">
                <PaperPlaneTilt size={14} weight="bold" /> One reminder for all {remindable.length}
              </button>
            )}
            {canChase && remindable.length > 0 && (
              <button type="button" onClick={remindAll} disabled={!!bulk || !!busyId} data-testid="aging-remind-all"
                title="Separate reminder email per invoice"
                className="h-9 px-3 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60">
                <PaperPlaneTilt size={14} weight="bold" /> {bulk ? `Sending ${bulk.done}/${bulk.total}…` : (client ? `Separate emails (${remindable.length})` : `Send reminders to all (${remindable.length})`)}
              </button>
            )}
            <button onClick={close} className="opacity-70 hover:opacity-100" data-testid="aging-popup-close"><X size={20} /></button>
          </div>
        </div>
        <div className="overflow-y-auto">
          {invoices.length === 0 ? (
            <div className="px-5 py-8 text-sm text-slate-400 text-center">Nothing in this range.</div>
          ) : (
            <table className="w-full text-sm" data-testid="aging-popup-table">
              <thead className="text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50 sticky top-0">
                <tr><th className="text-left px-5 py-2">Client</th><th className="text-left px-3 py-2">Invoice</th><th className="text-left px-3 py-2">Week ending</th><th className="text-right px-3 py-2">Age</th><th className="text-right px-3 py-2">Balance</th>{canChase && <th className="text-right px-5 py-2">Reminder</th>}</tr>
              </thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`aging-popup-row-${i.id}`}>
                    <td className="px-5 py-2 font-semibold text-slate-900">
                      <Link to={`/invoices?client=${i.client_id}`} className="hover:text-green-700">{i.client_name}</Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/invoices/${i.id}`} className="text-slate-700 hover:text-green-700 underline decoration-dotted">{i.number || "—"}</Link>
                      {i.status === "partially_paid" && <span className="ml-1 text-[10px] font-bold uppercase text-amber-700">partial</span>}
                      {i.status === "draft" && <span className="ml-1 text-[10px] font-bold uppercase text-slate-500">draft</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{i.week_ending || <span className="text-amber-700">undated</span>}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">{i.age_days == null ? "—" : `${i.age_days}d`}</td>
                    <td className={`px-3 py-2 text-right font-mono font-bold ${toneText[bucket.tone] || "text-slate-800"}`}>{money(i.balance_due)}</td>
                    {canChase && (
                      <td className="px-5 py-2 text-right whitespace-nowrap">
                        {i.can_remind ? (
                          <button type="button" onClick={() => remindOne(i)} disabled={!!busyId || !!bulk} data-testid={`aging-remind-${i.id}`}
                            title={lastReminded(i) ? `Last reminded ${new Date(lastReminded(i)).toLocaleString()}${i.reminders_count ? ` · ${i.reminders_count + (sentNow[i.id] ? 1 : 0)}× total` : ""}` : "Email the client a payment reminder for this invoice"}
                            className={`text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50 ${bucket.key === "over_90" ? "text-rose-700 hover:text-rose-900" : "text-orange-700 hover:text-orange-900"}`}>
                            <PaperPlaneTilt size={12} weight="bold" /> {busyId === i.id ? "Sending…" : (lastReminded(i) ? "Remind again" : "Send reminder")}
                          </button>
                        ) : (
                          <span className="text-[11px] text-slate-400" title={i.status === "draft" ? "Drafts can't be reminded — send the invoice first." : "Client has no billing email on file."}>{i.status === "draft" ? "draft" : "no email"}</span>
                        )}
                        {lastReminded(i) && <div className="text-[10px] text-slate-500" data-testid={`aging-reminded-${i.id}`}>{sentNow[i.id] ? "Sent just now" : `Last ${shortDT(lastReminded(i))}`}</div>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                <tr><td colSpan={4} className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-slate-600">Total</td><td className="px-3 py-2 text-right font-mono font-black">{money(total)}</td>{canChase && <td />}</tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
      {consolidated && client && (
        <ClientReminderModal client={client} invoices={remindable} minAgeDays={minAge}
          onClose={() => setConsolidated(false)}
          onSent={() => { setConsolidated(false); const now = new Date().toISOString(); setSentNow((p) => ({ ...p, ...Object.fromEntries(remindable.map((i) => [i.id, now])) })); onRefresh?.(); }} />
      )}
    </div>
  );
}

export function AgingBucketsCard({ data, onRefresh }) {
  const [openKey, setOpenKey] = useState(null);
  if (!data) return null;
  const invFor = (key) => (data.bucket_invoices || {})[key] || [];
  const hoverSummary = (key) => {
    const list = invFor(key);
    if (!list.length) return "No invoices in this range";
    const lines = list.slice(0, 8).map((i) => `${i.client_name} — ${i.number || "—"} — ${money(i.balance_due)}`);
    if (list.length > 8) lines.push(`…and ${list.length - 8} more`);
    return lines.join("\n") + "\n\nClick for the full list";
  };
  const openBucket = openKey === "undated" ? { key: "undated", label: "Undated", tone: "yellow" } : BUCKETS.find((b) => b.key === openKey);
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="aging-buckets-card">
      <div className="flex items-center justify-between mb-3">
        <div className="tinylabel">Receivables aging</div>
        <div className="text-xs text-slate-400" title="Age is measured from the invoice's week-ending date, not its due date — so an invoice can be 90+ here without being past due yet.">days since <b>week ending</b> (not due date) · click a range for details</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {BUCKETS.map((b) => (
          <button
            type="button"
            key={b.key}
            onClick={() => setOpenKey(b.key)}
            title={hoverSummary(b.key)}
            data-testid={`aging-bucket-${b.key}`}
            className={`border rounded-md p-3 text-left transition-shadow hover:shadow-md hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400 ${bucketBg[b.tone]}`}
          >
            <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">{b.label}</div>
            <div className="text-lg sm:text-xl font-black font-mono mt-1 truncate">{money(data.buckets[b.key])}</div>
            <div className="text-[10px] mt-0.5 opacity-70" data-testid={`aging-bucket-count-${b.key}`}>{invFor(b.key).length} invoice{invFor(b.key).length === 1 ? "" : "s"}</div>
          </button>
        ))}
      </div>
      {data.buckets.undated > 0 && (
        <button type="button" onClick={() => setOpenKey("undated")} data-testid="aging-bucket-undated"
          className="mt-3 w-full text-left flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-100">
          <WarningCircle size={14} className="text-amber-600 shrink-0" />
          <div>
            <b className="font-mono">{money(data.buckets.undated)}</b> in invoices are missing a <b>Week ending</b> date.{" "}
            Click to see them — open each one and use <b>Edit details</b> to bucket them correctly.
          </div>
        </button>
      )}
      {openBucket && (
        <AgingBucketPopup bucket={openBucket} invoices={invFor(openKey)} total={data.buckets[openKey]} onClose={() => setOpenKey(null)} onRefresh={onRefresh} />
      )}
    </div>
  );
}

export default function ReceivablesAging({ data: dataProp = null, onRefresh }) {
  const [dataInner, setDataInner] = useState(null);
  const loadInner = () => api.get("/reports/receivables-aging").then((r) => setDataInner(r?.data || null));
  useEffect(() => {
    if (dataProp) return;
    loadInner();
  }, [dataProp]);
  const data = dataProp || dataInner;
  const rows = useMemo(() => data?.by_client || [], [data]);
  const [cell, setCell] = useState(null); // {client_id, client_name, key}
  const [remindFor, setRemindFor] = useState(null); // by-client row
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const over90For = (cid) => ((data?.bucket_invoices || {}).over_90 || []).filter((i) => i.client_id === cid && i.can_remind);
  const cellInvoices = cell ? ((data?.bucket_invoices || {})[cell.key] || []).filter((i) => i.client_id === cell.client_id) : [];
  const cellBucket = cell ? BUCKETS.find((b) => b.key === cell.key) : null;
  const refresh = onRefresh || (dataProp ? undefined : loadInner);
  const Cell = ({ r, k, cls }) => {
    const v = r[k];
    if (!(v > 0)) return <td className="px-3 py-2 text-right font-mono text-slate-300">{money(v)}</td>;
    return (
      <td className="px-1 py-1 text-right">
        <button type="button" onClick={() => setCell({ client_id: r.client_id, client_name: r.client_name, key: k })} data-testid={`aging-cell-${r.client_id}-${k}`}
          title={`Show ${r.client_name}'s invoices in this range`}
          className={`w-full h-8 px-2 rounded text-right font-mono hover:bg-white hover:ring-1 hover:ring-slate-300 hover:shadow-sm ${cls}`}>{money(v)}</button>
      </td>
    );
  };

  return (
    <div className="mt-8">
      <div className="bg-white border border-slate-200 rounded-md" data-testid="aging-by-client-panel">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div>
            <div className="tinylabel">Outstanding by client</div>
            <div className="font-black text-slate-900">Receivables aging by client</div>
          </div>
          <div className="text-right text-xs text-slate-500">
            Age = days since <b>week ending</b>, not due date · click an amount for its invoices
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="aging-by-client-table">
            <thead className="text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Client</th>
                <th className="text-right px-3 py-2 border-r border-slate-200">Total</th>
                <th className="text-right px-3 py-2">&lt; 30</th>
                <th className="text-right px-3 py-2">30 – 60</th>
                <th className="text-right px-3 py-2">60 – 90</th>
                <th className="text-right px-3 py-2">90+</th>
                {isAdmin && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {!data && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Loading…</td></tr>
              )}
              {data && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Nothing outstanding.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.client_id} data-testid={`aging-row-${r.client_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/invoices?client=${r.client_id}`} className="font-semibold text-slate-900 hover:text-green-700">{r.client_name}</Link>
                    {r.undated > 0 && (
                      <span className="ml-2 text-[11px] text-amber-700" title={`${money(r.undated)} on undated invoices`}>
                        · {money(r.undated)} undated
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-slate-900 border-r border-slate-100" data-testid={`aging-total-${r.client_id}`}>{money(r.total)}</td>
                  <Cell r={r} k="under_30" cls="text-slate-800" />
                  <Cell r={r} k="30_60" cls="text-amber-700 font-semibold" />
                  <Cell r={r} k="60_90" cls="text-orange-700 font-bold" />
                  <Cell r={r} k="over_90" cls="text-rose-700 font-black" />
                  {isAdmin && (
                    <td className="px-3 py-1 text-right whitespace-nowrap">
                      {r.over_90 > 0 && (
                        over90For(r.client_id).length > 0 ? (
                          <button type="button" onClick={() => setRemindFor(r)} data-testid={`aging-remind-90-${r.client_id}`}
                            title={`Email ${r.client_name} one reminder listing every invoice 90+ days old`}
                            className="text-xs font-bold text-rose-700 hover:text-rose-900 inline-flex items-center gap-1">
                            <PaperPlaneTilt size={12} weight="bold" /> Remind 90+
                          </button>
                        ) : <span className="text-[11px] text-slate-400" title="No billing email on file, or only drafts">no email</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {data && rows.length > 0 && (
              <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                <tr>
                  <td className="px-4 py-2 font-bold text-slate-700 uppercase text-xs tracking-widest">Total</td>
                  <td className="px-3 py-2 text-right font-mono font-black text-slate-900 border-r border-slate-200" data-testid="aging-grand-total">{money(data.total_outstanding)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{money(data.buckets.under_30)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-amber-700">{money(data.buckets["30_60"])}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-orange-700">{money(data.buckets["60_90"])}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-rose-700">{money(data.buckets.over_90)}</td>
                  {isAdmin && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {cell && cellBucket && (
        <AgingBucketPopup bucket={cellBucket} invoices={cellInvoices} total={cellInvoices.reduce((s, i) => s + i.balance_due, 0)}
          clientName={cell.client_name} client={rows.find((r) => r.client_id === cell.client_id)} onClose={() => setCell(null)} onRefresh={refresh} />
      )}
      {remindFor && (
        <ClientReminderModal client={remindFor} invoices={over90For(remindFor.client_id)} minAgeDays={90}
          onClose={() => setRemindFor(null)} onSent={() => { setRemindFor(null); refresh?.(); }} />
      )}
    </div>
  );
}
